// ============================================================================
// ParticleField — interactive canvas backdrop for the landing hero.
//
// Renders a network of slow-drifting particles connected by faint lines
// when they pass close to each other ("constellation"). The cursor
// gently repels nearby particles and lights up its local neighbourhood,
// so the field feels alive without distracting from the foreground copy.
//
// Performance notes:
//   • Capped at ~88 particles regardless of screen size (cuts to ~55 on
//     small viewports so phones don't choke).
//   • Single requestAnimationFrame loop; canvas is sized once + on resize.
//   • Devicepixelratio-aware so it stays crisp on retina without paying
//     the cost of a 4× internal canvas on regular displays.
//   • prefers-reduced-motion users get a stationary, lighter field.
//
// Positioning: render this with `position: absolute; inset: 0; z-index: 0`
// inside the hero section, then put your foreground content with
// `position: relative; z-index: 1`.
// ============================================================================

import { useEffect, useRef } from 'react';

const ACCENT = { r: 91, g: 106, b: 240 };   // var(--accent) in RGB
const GREEN  = { r: 34, g: 197, b: 94 };

export default function ParticleField({ density = 1, showCandles = true }) {
  const canvasRef = useRef(null);
  const cursorRef = useRef({ x: -1000, y: -1000, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    let width  = 0;
    let height = 0;
    let dpr    = 1;
    let particles = [];
    let candles   = [];
    let raf       = 0;
    let lastTs    = performance.now();

    const sizeFromViewport = () => {
      const w = canvas.clientWidth  || window.innerWidth;
      const h = canvas.clientHeight || window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width  = w;
      height = h;
      canvas.width  = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const spawn = () => {
      // Density scales with surface area but caps so we don't tank perf.
      const isSmall = width < 720;
      const base    = isSmall ? 55 : 88;
      const target  = Math.max(28, Math.round(base * density));
      particles = Array.from({ length: target }, () => ({
        x:  Math.random() * width,
        y:  Math.random() * height,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        r:  Math.random() * 1.6 + 0.6,
      }));

      // A few faint drifting candlesticks tucked behind the particles,
      // for a quiet on-brand reference.
      if (showCandles && !isSmall) {
        candles = Array.from({ length: 6 }, () => ({
          x:    Math.random() * width,
          y:    Math.random() * height,
          drift:(Math.random() - 0.5) * 0.05,
          size: 28 + Math.random() * 36,
          up:   Math.random() > 0.5,
          alpha:0.04 + Math.random() * 0.05,
          rot:  (Math.random() - 0.5) * 0.15,
        }));
      } else {
        candles = [];
      }
    };

    const onResize = () => { sizeFromViewport(); spawn(); };

    const onPointer = (e) => {
      const rect = canvas.getBoundingClientRect();
      cursorRef.current.x = e.clientX - rect.left;
      cursorRef.current.y = e.clientY - rect.top;
      cursorRef.current.active = true;
    };
    const onPointerLeave = () => {
      cursorRef.current.active = false;
      cursorRef.current.x = -1000;
      cursorRef.current.y = -1000;
    };

    const drawCandle = (c) => {
      const col = c.up ? GREEN : { r: 239, g: 68, b: 68 };
      ctx.save();
      ctx.translate(c.x, c.y);
      ctx.rotate(c.rot);
      ctx.strokeStyle = `rgba(${col.r},${col.g},${col.b},${c.alpha * 1.4})`;
      ctx.fillStyle   = `rgba(${col.r},${col.g},${col.b},${c.alpha})`;
      ctx.lineWidth   = 1;
      // wick
      ctx.beginPath();
      ctx.moveTo(0, -c.size / 2);
      ctx.lineTo(0,  c.size / 2);
      ctx.stroke();
      // body
      const bw = c.size * 0.35;
      const bh = c.size * 0.62;
      ctx.fillRect(-bw / 2, -bh / 2, bw, bh);
      ctx.restore();
    };

    const frame = (ts) => {
      const dt = Math.min(48, ts - lastTs);
      lastTs   = ts;

      ctx.clearRect(0, 0, width, height);

      // background candles drift slowly across
      for (const c of candles) {
        c.x += c.drift * dt;
        if (c.x < -60)        c.x = width + 60;
        if (c.x > width + 60) c.x = -60;
        drawCandle(c);
      }

      const cx = cursorRef.current.x;
      const cy = cursorRef.current.y;
      const cursorOn = cursorRef.current.active;
      const repelR  = 110;
      const repelR2 = repelR * repelR;

      // update + draw particles
      for (const p of particles) {
        if (!reduceMotion) {
          p.x += p.vx * dt * 0.6;
          p.y += p.vy * dt * 0.6;

          // gentle cursor repel
          if (cursorOn) {
            const dx = p.x - cx;
            const dy = p.y - cy;
            const d2 = dx * dx + dy * dy;
            if (d2 < repelR2 && d2 > 0.001) {
              const d    = Math.sqrt(d2);
              const push = (repelR - d) / repelR;
              p.vx += (dx / d) * push * 0.02;
              p.vy += (dy / d) * push * 0.02;
            }
          }

          // soft velocity damping so cursor pushes don't accumulate
          p.vx *= 0.985;
          p.vy *= 0.985;

          // wrap around the viewport
          if (p.x < -20)         p.x = width + 20;
          if (p.x > width + 20)  p.x = -20;
          if (p.y < -20)         p.y = height + 20;
          if (p.y > height + 20) p.y = -20;
        }

        // brightness lifts near the cursor
        let bright = 0.42;
        if (cursorOn) {
          const dx = p.x - cx;
          const dy = p.y - cy;
          const d2 = dx * dx + dy * dy;
          if (d2 < repelR2) bright = 0.42 + (1 - d2 / repelR2) * 0.5;
        }

        ctx.fillStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${bright})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // constellation lines — only between particles within LINK_R, and
      // only check pairs once. O(n²) but n≤88 so it's cheap.
      const LINK_R  = 130;
      const LINK_R2 = LINK_R * LINK_R;
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const b  = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > LINK_R2) continue;
          const alpha = (1 - d2 / LINK_R2) * 0.22;
          ctx.strokeStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${alpha})`;
          ctx.lineWidth   = 0.6;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      // cursor halo
      if (cursorOn) {
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 140);
        grad.addColorStop(0,   `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},0.16)`);
        grad.addColorStop(1,   `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},0)`);
        ctx.fillStyle = grad;
        ctx.fillRect(cx - 140, cy - 140, 280, 280);
      }

      raf = requestAnimationFrame(frame);
    };

    sizeFromViewport();
    spawn();
    window.addEventListener('resize', onResize);
    canvas.addEventListener('pointermove', onPointer);
    canvas.addEventListener('pointerleave', onPointerLeave);
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      canvas.removeEventListener('pointermove', onPointer);
      canvas.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [density, showCandles]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        pointerEvents: 'auto', // so we receive pointermove
        zIndex: 0, display: 'block',
      }}
    />
  );
}
